import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "required",
    "submitButton",
    "modalContainer",
    "formContainer",
    "datesCheckbox",
    "replacementDatesContainer",
    "replacementStartDate",
    "replacementEndDate",
    "validationMessage",
    "myCoursesSelect",
    "allCoursesSelect",
    "nameInput",
    "responsiblePersonSelect",
    "nameFieldError",
    "responsiblePersonFieldError",
    "emptyFormTemplate",
  ];

  connect() {
    this.attemptedSubmit = false;

    this.element.addEventListener("turbo:submit-end", (event) => {
      if (!event.target.closest?.(".course-duplicate-modal__form")) return;

      if (event.detail.success) {
        this.showModal();
      }
    });

    this.requiredTargets.forEach((checkbox) => {
      checkbox.addEventListener("change", () => this.validateForm());
    });
    this.toggleReplacementDates();
    this.scheduleValidateForm();
  }

  scheduleValidateForm() {
    if (this.validateFormFrame) cancelAnimationFrame(this.validateFormFrame);

    this.validateFormFrame = requestAnimationFrame(() => {
      this.validateFormFrame = null;
      this.validateForm();
    });
  }

  nameInputTargetConnected() {
    this.scheduleValidateForm();
  }

  responsiblePersonSelectTargetConnected() {
    this.scheduleValidateForm();
  }

  replacementStartDateTargetConnected() {
    this.scheduleValidateForm();
  }

  replacementEndDateTargetConnected() {
    this.scheduleValidateForm();
  }

  submitButtonTargetConnected() {
    this.scheduleValidateForm();
  }

  showModal() {
    if (!this.hasModalContainerTarget) return;
    this.modalContainerTarget.classList.remove("hidden");
  }

  hideModal(event) {
    event.preventDefault();
    if (!this.hasModalContainerTarget) return;
    this.modalContainerTarget.classList.add("hidden");
  }

  validateForm() {
    this.syncDatesCopyState();
    const validationErrors = this.validationErrors();
    const showFieldErrors = this.attemptedSubmit;

    this.updateSubmitButton(validationErrors.length === 0);
    this.updateInlineFieldErrors(showFieldErrors);

    if (showFieldErrors) {
      this.updateValidationMessage(validationErrors);
    } else {
      this.updateValidationMessage([]);
    }
  }

  toggleReplacementDates() {
    if (!this.hasReplacementDatesContainerTarget || !this.hasDatesCheckboxTarget) {
      this.validateForm();
      return;
    }

    const showReplacementDates = this.requiresReplacementDates();
    this.replacementDatesContainerTarget.classList.toggle("hidden", !showReplacementDates);

    if (this.hasReplacementStartDateTarget) {
      this.setInputRequired(this.replacementStartDateTarget, showReplacementDates);
    }

    if (this.hasReplacementEndDateTarget) {
      this.setInputRequired(this.replacementEndDateTarget, showReplacementDates);
    }

    this.validateForm();
  }

  requiresReplacementDates() {
    if (!this.hasDatesCheckboxTarget) return false;

    return this.sourceDatesExpired() || !this.datesCopySelected();
  }

  sourceDatesExpired() {
    if (!this.hasDatesCheckboxTarget) return false;

    return this.datesCheckboxTarget.dataset.sourceDatesExpired === "true";
  }

  replacementDatesPresent() {
    if (!this.hasReplacementStartDateTarget || !this.hasReplacementEndDateTarget) {
      return false;
    }

    return this.fieldValue(this.replacementStartDateTarget).length > 0 &&
      this.fieldValue(this.replacementEndDateTarget).length > 0;
  }

  copyDatesSelectedAndAllowed() {
    if (!this.hasDatesCheckboxTarget) return false;

    return !this.sourceDatesExpired() && this.datesCopySelected();
  }

  replacementEndDateNotInPast() {
    if (!this.hasReplacementEndDateTarget) return true;

    const endValue = this.fieldValue(this.replacementEndDateTarget);
    if (!endValue) return true;

    const wrapper =
      this.replacementEndDateTarget.closest?.(".date-input-aq") || this.replacementEndDateTarget;
    const input = this.controlInput(this.replacementEndDateTarget);
    const min = input?.min || wrapper.dataset?.minDate;
    if (!min) return true;

    const endIso = this.toIsoDate(endValue);
    const minIso = this.toIsoDate(min);
    if (!this.isIsoDate(endIso) || !this.isIsoDate(minIso)) return true;

    return endIso >= minIso;
  }

  replacementEndDateOnOrAfterStartDate() {
    if (!this.hasReplacementStartDateTarget || !this.hasReplacementEndDateTarget) {
      return true;
    }

    const startIso = this.toIsoDate(this.fieldValue(this.replacementStartDateTarget));
    const endIso = this.toIsoDate(this.fieldValue(this.replacementEndDateTarget));
    if (!this.isIsoDate(startIso) || !this.isIsoDate(endIso)) return true;

    return endIso >= startIso;
  }

  validationErrors() {
    const errors = [];

    this.requiredTargets.forEach((field) => {
      if (!this.requiredFieldSelected(field)) {
        errors.push(this.labelForRequiredField(field));
      }
    });

    if (this.hasNameInputTarget && this.fieldValue(this.nameInputTarget).length === 0) {
      errors.push("New Course Name");
    }

    if (this.hasResponsiblePersonSelectTarget && !this.responsiblePersonSelected()) {
      errors.push("Responsible Person");
    }

    if (this.copyDatesSelectedAndAllowed()) {
      return errors;
    }

    if (this.hasReplacementStartDateTarget && this.fieldValue(this.replacementStartDateTarget).length === 0) {
      errors.push("New Start Date");
    }

    if (this.hasReplacementEndDateTarget && this.fieldValue(this.replacementEndDateTarget).length === 0) {
      errors.push("New End Date");
    }

    if (this.replacementDatesPresent() && !this.replacementEndDateNotInPast()) {
      errors.push("New End Date cannot be in the past");
    }

    if (this.replacementDatesPresent() && !this.replacementEndDateOnOrAfterStartDate()) {
      errors.push("New End Date must be on or after New Start Date");
    }

    return [...new Set(errors)];
  }

  labelForRequiredField(field) {
    const labels = {
      duplicate_name: "New Course Name",
      duplicate_content: "Content",
      duplicate_responsible_person: "Responsible Person",
    };

    return labels[field.id] || "Required field";
  }

  responsiblePersonSelected() {
    if (!this.hasResponsiblePersonSelectTarget) return false;

    const field = this.responsiblePersonSelectTarget;
    if (field.tagName === "SELECT") {
      return field.value.trim().length > 0;
    }

    const hiddenInput =
      field.matches?.('input[type="hidden"]') ? field : field.querySelector?.('input[type="hidden"]');

    return (hiddenInput?.value || this.fieldValue(field)).trim().length > 0;
  }

  updateValidationMessage(validationErrors) {
    if (!this.hasValidationMessageTarget) return;

    if (validationErrors.length === 0) {
      this.validationMessageTarget.textContent = "";
      this.validationMessageTarget.classList.add("hidden");
      return;
    }

    this.validationMessageTarget.textContent = `Complete required fields: ${validationErrors.join(", ")}.`;
    this.validationMessageTarget.classList.remove("hidden");
  }

  updateSubmitButton(enabled) {
    if (!this.hasSubmitButtonTarget) return;

    this.submitButtonTarget.disabled = !enabled;
    this.submitButtonTarget.setAttribute("aria-disabled", enabled ? "false" : "true");
  }

  inlineFieldErrors() {
    const errors = {
      name: null,
      responsiblePerson: null,
      startDate: null,
      endDate: null,
    };

    if (this.hasNameInputTarget && this.fieldValue(this.nameInputTarget).length === 0) {
      errors.name = "Enter a new course name.";
    }

    if (this.hasResponsiblePersonSelectTarget && !this.responsiblePersonSelected()) {
      errors.responsiblePerson = "Select a responsible person.";
    }

    if (this.copyDatesSelectedAndAllowed()) {
      return errors;
    }

    if (this.hasReplacementStartDateTarget && this.fieldValue(this.replacementStartDateTarget).length === 0) {
      errors.startDate = "Enter a start date.";
    }

    if (this.hasReplacementEndDateTarget && this.fieldValue(this.replacementEndDateTarget).length === 0) {
      errors.endDate = "Enter an end date.";
    } else if (this.replacementDatesPresent() && !this.replacementEndDateNotInPast()) {
      errors.endDate = "End date cannot be in the past.";
    } else if (this.replacementDatesPresent() && !this.replacementEndDateOnOrAfterStartDate()) {
      errors.endDate = "End date must be on or after the start date.";
    }

    return errors;
  }

  updateInlineFieldErrors(show) {
    const errors = show ? this.inlineFieldErrors() : {
      name: null,
      responsiblePerson: null,
      startDate: null,
      endDate: null,
    };

    this.setNamedFieldError(this.hasNameFieldErrorTarget ? this.nameFieldErrorTarget : null, errors.name, () => {
      if (!this.hasNameInputTarget) return null;
      return this.nameInputTarget.tagName === "INPUT" ? this.nameInputTarget : this.nameInputTarget.querySelector("input");
    }, (input, message) => {
      input.classList.toggle("form-input-error", Boolean(message));
    });

    this.setNamedFieldError(
      this.hasResponsiblePersonFieldErrorTarget ? this.responsiblePersonFieldErrorTarget : null,
      errors.responsiblePerson,
      () => {
        if (!this.hasResponsiblePersonSelectTarget) return null;
        const field = this.responsiblePersonSelectTarget.closest(".dropdown-field");
        return field?.querySelector(".dropdown-field-control") || null;
      },
      (trigger, message) => {
        trigger.classList.toggle("dropdown-field-control-error", Boolean(message));
      }
    );

    this.setDateFieldError(
      this.hasReplacementStartDateTarget ? this.replacementStartDateTarget : null,
      errors.startDate
    );
    this.setDateFieldError(
      this.hasReplacementEndDateTarget ? this.replacementEndDateTarget : null,
      errors.endDate
    );
  }

  setNamedFieldError(errorElement, message, inputResolver, styleInput) {
    const input = inputResolver();
    if (input) {
      if (message) {
        input.setAttribute("aria-invalid", "true");
      } else {
        input.removeAttribute("aria-invalid");
      }
      styleInput(input, message);
    }

    if (!errorElement) return;

    if (message) {
      errorElement.textContent = message;
      errorElement.classList.remove("hidden");
    } else {
      errorElement.textContent = "";
      errorElement.classList.add("hidden");
    }
  }

  setDateFieldError(dateTarget, message) {
    if (!dateTarget) return;

    const wrapper = dateTarget.closest(".date-input-aq") || dateTarget;
    const input = this.dateInput(dateTarget);

    if (input) {
      if (message) {
        input.setAttribute("aria-invalid", "true");
      } else {
        input.removeAttribute("aria-invalid");
      }
    }

    wrapper.classList.toggle("date-input-aq--error", Boolean(message));

    let errorElement = wrapper.querySelector("[data-course-duplicate-modal-inline-error]");
    if (!errorElement) {
      errorElement = document.createElement("p");
      errorElement.className =
        "duplicate-course-modal__field-error dropdown-field-help dropdown-field-help-error hidden";
      errorElement.dataset.courseDuplicateModalInlineError = "true";
      errorElement.setAttribute("role", "alert");
      wrapper.appendChild(errorElement);
    }

    if (message) {
      errorElement.textContent = message;
      errorElement.classList.remove("hidden");
    } else {
      errorElement.textContent = "";
      errorElement.classList.add("hidden");
    }
  }

  submitForm(event) {
    this.attemptedSubmit = true;
    const validationErrors = this.validationErrors();

    if (validationErrors.length > 0) {
      event.preventDefault();
      this.validateForm();
    } else {
      this.updateValidationMessage([]);
      this.updateInlineFieldErrors(false);
    }
  }

  async loadModal(event) {
    const courseId = event.target.value;

    if (!courseId) {
      this.clearLoadedForm();
      return;
    }

    await this.loadModalForCourseId(courseId);
  }

  scopeChanged(event) {
    const myScope = event.target.value === "1";

    if (this.hasMyCoursesSelectTarget) {
      this.myCoursesSelectTarget.classList.toggle("hidden", !myScope);
    }

    if (this.hasAllCoursesSelectTarget) {
      this.allCoursesSelectTarget.classList.toggle("hidden", myScope);
    }

    this.clearLoadedForm();
    this.attemptedSubmit = false;
    this.updateValidationMessage([]);

    const courseId = this.visibleCourseId();
    if (courseId) {
      this.loadModalForCourseId(courseId);
    }
  }

  visibleCourseId() {
    const wrap = this.visibleCourseSelectWrap();
    if (!wrap) return "";

    return (wrap.querySelector('input[type="hidden"]')?.value || "").trim();
  }

  visibleCourseSelectWrap() {
    if (this.hasMyCoursesSelectTarget && !this.myCoursesSelectTarget.classList.contains("hidden")) {
      return this.myCoursesSelectTarget;
    }

    if (this.hasAllCoursesSelectTarget && !this.allCoursesSelectTarget.classList.contains("hidden")) {
      return this.allCoursesSelectTarget;
    }

    return null;
  }

  async loadModalForCourseId(courseId) {
    const container = this.formOrModalContainer();
    if (!container) return;

    try {
      if (this.hasModalContainerTarget && !this.hasFormContainerTarget) {
        this.modalContainerTarget.classList.remove("hidden");
      }

      const response = await fetch(this.duplicateModalUrl(courseId));
      const html = await response.text();
      container.innerHTML = html;
      this.attemptedSubmit = false;
      this.scheduleValidateForm();
    } catch (e) {
      console.error("Failed to load modal", e);
    }
  }

  toIsoDate(value) {
    const normalized = String(value || "").trim();
    if (!normalized) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return normalized;

    const match = normalized.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return normalized;

    const [, month, day, year] = match;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }

  isIsoDate(value) {
    return /^\d{4}-\d{2}-\d{2}$/.test(value);
  }

  formOrModalContainer() {
    if (this.hasFormContainerTarget) return this.formContainerTarget;
    if (this.hasModalContainerTarget) return this.modalContainerTarget;
    return null;
  }

  duplicateModalUrl(courseId) {
    const path = `/document_sets/${courseId}/duplicate_modal`;
    if (!this.hasFormContainerTarget) return path;

    const params = new URLSearchParams({ layout: "design_system" });
    const { duplicateCourseFormId: formId, duplicateCourseIdentityFieldsClass: identityFieldsClass } =
      this.formContainerTarget.dataset;
    if (formId) params.set("form_id", formId);
    if (identityFieldsClass) params.set("identity_fields_class", identityFieldsClass);

    return `${path}?${params.toString()}`;
  }

  clearLoadedForm() {
    if (this.hasFormContainerTarget) {
      if (this.hasEmptyFormTemplateTarget) {
        this.formContainerTarget.innerHTML = this.emptyFormTemplateTarget.innerHTML;
      } else {
        this.formContainerTarget.innerHTML = "";
      }
    }
    if (this.hasModalContainerTarget) this.modalContainerTarget.innerHTML = "";
    this.scheduleValidateForm();
  }

  dateInput(target) {
    return this.controlInput(target);
  }

  controlInput(target) {
    if (!target) return null;
    if (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA") {
      return target;
    }

    return (
      target.querySelector(".date-input-aq__field") ||
      target.querySelector('input[type="hidden"]') ||
      target.querySelector("input, select, textarea")
    );
  }

  fieldValue(target) {
    return (this.controlInput(target)?.value || "").trim();
  }

  setInputRequired(target, required) {
    const input = this.dateInput(target);
    if (input) input.required = required;
  }

  requiredFieldSelected(field) {
    if (field.type === "checkbox") return field.checked;
    return field.value === "1";
  }

  datesCopySelected() {
    if (!this.hasDatesCheckboxTarget) return false;
    const field = this.datesCheckboxTarget;
    if (field.type === "checkbox") return field.checked;
    return field.value === "1";
  }

  setDatesCopySelected(selected) {
    if (!this.hasDatesCheckboxTarget) return;
    const field = this.datesCheckboxTarget;
    if (field.type === "checkbox") {
      field.checked = selected;
    } else {
      field.value = selected ? "1" : "0";
    }
  }

  syncDatesCopyState() {
    if (!this.hasDatesCheckboxTarget || !this.hasReplacementStartDateTarget || !this.hasReplacementEndDateTarget) {
      return;
    }

    if (this.sourceDatesExpired()) {
      this.setDatesCopySelected(false);
      return;
    }

    const sourceStart = this.datesCheckboxTarget.dataset.sourceStartDate;
    const sourceEnd = this.datesCheckboxTarget.dataset.sourceEndDate;
    if (!sourceStart || !sourceEnd) {
      return;
    }

    const startVal = this.fieldValue(this.replacementStartDateTarget);
    const endVal = this.fieldValue(this.replacementEndDateTarget);
    if (!startVal || !endVal) {
      return;
    }

    const same =
      this.toIsoDate(startVal) === sourceStart &&
      this.toIsoDate(endVal) === sourceEnd;
    this.setDatesCopySelected(same);
  }
}
